import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from "@angular/forms";
import { CommonModule } from '@angular/common';

import { TeamCheckService } from '../../features/video/controller/team-check.service';
import { SelectorModel } from '../../fragments/selector/selector-model';
import { TeamCheckInfoResponse, CreateTeamRequest } from '@solverml/api';
import { VideoLabService } from '../../features/video/controller/video-lab.service';

// UI Components
import { RfebmPrimaryButtonComponent } from "../../ui/atoms/rfebm-primary-button/rfebm-primary-button.component";
import { RfebmSecondaryButtonComponent } from "../../ui/atoms/rfebm-secondary-button/rfebm-secondary-button.component";
import { SelectorComponent } from "../../fragments/selector/selector.component";

@Component({
  selector: 'app-team-check-page',
  templateUrl: './team-check-page.component.html',
  styleUrls: ['./team-check-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RfebmPrimaryButtonComponent,
    RfebmSecondaryButtonComponent,
    FormsModule,
    SelectorComponent
  ]
})
export class TeamCheckPageComponent implements OnInit {
  videoId: string = '';
  info: TeamCheckInfoResponse | null = null;

  loading = true;
  saving = false;

  // Estado para controlar si falta la imagen
  imageNotReady = false;

  showCreateModal = false;
  creatingTeam = false;
  isFullscreenImage = false;
  isInternalUser = false;

  newTeamData: CreateTeamRequest = { name: '', stadium: '', location: '' };

  selectedRedTeamId: string | null = null;
  selectedBlueTeamId: string | null = null;

  teamsOptions: SelectorModel[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private teamCheckService: TeamCheckService,
    private videoLabService: VideoLabService,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit(): Promise<void> {
    this.videoId = this.route.snapshot.paramMap.get('id') || '';
    this.videoLabService.checkInternalAccess().subscribe(isInternal => {
      this.isInternalUser = isInternal;
    });
    if (this.videoId) {
      // Cargamos info y equipos en paralelo
      await Promise.all([this.loadData(), this.loadTeams()]);
    } else {
      this.showMessage('No se ha encontrado el ID del video');
      this.router.navigate(['/analysis']);
    }
  }

  async loadData(): Promise<void> {
    try {
      this.loading = true;
      this.info = await this.teamCheckService.getInfo(this.videoId);

      if (this.info?.team_check_completed) {
        this.router.navigate(['/analysis', this.videoId]);
        return;
      }

      // Verificamos si existe la imagen. Si es null o string vacío, activamos el estado de espera.
      if (!this.info || !this.info.detection_image_url) {
        this.imageNotReady = true;
      } else {
        this.imageNotReady = false;
      }

    } catch (error) {
      console.error(error);
      this.showMessage('Error cargando información del video');
    } finally {
      this.loading = false;
    }
  }

  async loadTeams(): Promise<void> {
    try {
      const teams = await this.teamCheckService.getAvailableTeams(this.videoId);
      this.teamsOptions = teams.map(t => ({
        id: t.id, value: t.id, label: t.name, disabled: false
      }));
    } catch (error) {
      console.error(error);
      this.showMessage('Error cargando equipos disponibles');
    }
  }

  // --- LÓGICA UI ---

  toggleFullscreenImage(): void {
    if (!this.imageNotReady) {
      this.isFullscreenImage = !this.isFullscreenImage;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if (this.isFullscreenImage) this.isFullscreenImage = false;
    if (this.showCreateModal) this.closeCreateModal();
  }

  onRedTeamSelected(option: SelectorModel | null): void {
    this.selectedRedTeamId = option ? option.value : null;
  }

  onBlueTeamSelected(option: SelectorModel | null): void {
    this.selectedBlueTeamId = option ? option.value : null;
  }

  // --- MODAL ---

  openCreateModal(): void {
    this.newTeamData = { name: '', stadium: '', location: '' };
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  async createTeam(): Promise<void> {
    if (!this.newTeamData.name.trim()) {
      this.showMessage('El nombre del equipo es obligatorio');
      return;
    }
    try {
      this.creatingTeam = true;
      await this.teamCheckService.createTeam(this.videoId, this.newTeamData);
      await this.loadTeams();
      this.showMessage(`Equipo creado correctamente`);
      this.closeCreateModal();
    } catch (error) {
      console.error(error);
      this.showMessage('Error al crear el equipo');
    } finally {
      this.creatingTeam = false;
    }
  }

  async confirm(): Promise<void> {
    if (!this.selectedRedTeamId || !this.selectedBlueTeamId) {
      this.showMessage('Por favor, selecciona ambos equipos');
      return;
    }
    if (this.selectedRedTeamId === this.selectedBlueTeamId) {
      this.showMessage('El equipo Rojo y el Azul no pueden ser el mismo');
      return;
    }
    try {
      this.saving = true;
      await this.teamCheckService.confirmTeams(this.videoId, this.selectedRedTeamId, this.selectedBlueTeamId);
      this.showMessage('Equipos asignados correctamente');
      this.router.navigate(['/analysis']);
    } catch (error) {
      console.error(error);
      this.showMessage('Error al guardar la asignación');
    } finally {
      this.saving = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/analysis']);
  }

  openWatchWithoutTeams(): void {
    if (!this.videoId) {
      return;
    }

    this.router.navigate(['/watch', this.videoId], {
      queryParams: { internal_bypass: '1' }
    });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }
}
